import type { Distribution } from "@memorang/types";
import type {
	Milestone,
	MiniStat,
} from "@memorang/ui/components/LessonDetailStatsTab/types";
import type {
	LessonDetailMetaChip,
	ModuleArtifact,
} from "@memorang/ui/components/LessonDetailViewScreen/types";
import type { Lesson } from "@memorang/ui/components/LessonsHomeScreen/types";

export const allModules: Lesson[] = [
	{
		id: "sepsis-recognition-first-hour",
		title: "Sepsis Recognition and First-Hour Response",
		description:
			"An updated review of early warning signs, lactate trends, and rapid response priorities.",
		meta: { specialty: "Emergency Medicine", durationMinutes: 22 },
		thumbnailUrl:
			"https://images.unsplash.com/photo-1576091160550-2173dba999ef?auto=format&fit=crop&w=400&q=80",
	},
	{
		id: "hypertension-management-high-risk",
		title: "Hypertension Management in High-Risk Adult Populations",
		description:
			"Compare guideline-directed therapy adjustments across common comorbidities.",
		meta: { specialty: "Primary Care", durationMinutes: 19 },
		thumbnailUrl:
			"https://images.unsplash.com/photo-1584515933487-779824d29309?auto=format&fit=crop&w=400&q=80",
	},
	{
		id: "migraine-red-flags-imaging",
		title: "Migraine Red Flags That Require Immediate Imaging",
		description:
			"Learn the symptom patterns and exam findings that change workup urgency.",
		meta: { specialty: "Neurology", durationMinutes: 12 },
		thumbnailUrl:
			"https://images.unsplash.com/photo-1516549655169-df83a0774514?auto=format&fit=crop&w=400&q=80",
	},
];

export const exploreModules: Lesson[] = [
	...allModules,
	{
		id: "chronic-heart-failure-staging",
		title: "Chronic Heart Failure: Staging and Long-Term Management",
		description:
			"An overview of heart failure classification by ejection fraction and the four pillars of guideline-directed medical therapy.",
		meta: { specialty: "Cardiology", durationMinutes: 25 },
		thumbnailUrl:
			"https://images.unsplash.com/photo-1576091160550-2173dba999ef?auto=format&fit=crop&w=400&q=80",
	},
	{
		id: "type-2-diabetes-pharmacologic-management",
		title: "Type 2 Diabetes: From Metformin to Advanced Therapy",
		description:
			"A comprehensive overview of diagnostic criteria, individualized glycemic targets, and pharmacologic management.",
		meta: { specialty: "Endocrinology", durationMinutes: 25 },
		thumbnailUrl:
			"https://images.unsplash.com/photo-1584515933487-779824d29309?auto=format&fit=crop&w=400&q=80",
	},
];

export type ExploreLessonDetails = {
	progressPercent: number;
};

export const exploreLessonDetails: Record<string, ExploreLessonDetails> = {
	"sepsis-recognition-first-hour": { progressPercent: 100 },
	"hypertension-management-high-risk": { progressPercent: 0 },
	"migraine-red-flags-imaging": { progressPercent: 45 },
	"chronic-heart-failure-staging": { progressPercent: 0 },
	"type-2-diabetes-pharmacologic-management": { progressPercent: 100 },
};

export type SuggestedModule = {
	id: string;
	title: string;
	description: string;
	specialty: string;
	durationMinutes: number;
	completionPercent: number;
};

export const suggestedModules: SuggestedModule[] = [
	{
		id: "migraine-red-flags-imaging",
		title: "Migraine Red Flags That Require Immediate Imaging",
		description:
			"One required step remains. Finish the assessment to earn 0.5 CME credits and generate your module certificate.",
		specialty: "Neurology",
		durationMinutes: 12,
		completionPercent: 45,
	},
	{
		id: "chronic-heart-failure-staging",
		title: "Chronic Heart Failure: Staging and Long-Term Management",
		description:
			"Continue with a focused Cardiology module covering diagnosis, treatment selection, and longitudinal management.",
		specialty: "Cardiology",
		durationMinutes: 25,
		completionPercent: 0,
	},
];

export const moduleHomeStats = {
	modulesDone: 2,
	dayStreak: 4,
	minutesToday: 47,
};

export const moduleHomeDistribution: Distribution = {
	high: 2,
	medium: 1,
	low: 0,
	none: 2,
};

export const moduleHomeSubtitle =
	"Complete your CME evaluation to earn credit.";

export const moduleHomeInsight =
	"You finished the Sepsis Recognition learning activities. Complete the required CME evaluation to earn 0.5 credits and generate your certificate; then continue with Chronic Heart Failure.";

export const moduleDetailArtifacts: ModuleArtifact[] = [
	{
		id: "overview",
		title: "Overview",
		description:
			"Key updates in cardiovascular risk assessment and preventive treatment planning.",
		statusLabel: "Start here",
		status: "completed",
		iconName: "file-document-outline",
	},
	{
		id: "imaging-the-knee",
		title: "Imaging the Knee",
		description:
			"Case-based imaging review focused on patient presentation and follow-up decisions.",
		statusLabel: "Audio review",
		status: "completed",
		iconName: "play-circle-outline",
		creditLabel: "0.25 cr",
	},
	{
		id: "study-guide",
		title: "Comprehensive Study Guide",
		description:
			"Annotated visual summary covering diagnosis criteria, guideline shifts, and next steps.",
		statusLabel: "Study guide",
		status: "next",
		iconName: "book-open-page-variant-outline",
		creditLabel: "0.25 cr",
	},
	{
		id: "module-assessment-quiz",
		title: "Module Assessment Quiz",
		description:
			"Evidence-based knowledge check to confirm retention before certificate release.",
		statusLabel: "Assessment",
		status: "locked",
		iconName: "clipboard-text-outline",
		creditLabel: "1.0 cr",
	},
];

export const moduleDetailProgressPercent = 50;

export const moduleDetailMetaChips: LessonDetailMetaChip[] = [
	{ label: "Cardiology" },
	{ iconName: "clock-outline", label: "4 min" },
	{ iconName: "certificate-outline", label: "0.5 CME credits total" },
];

export const moduleDetailMiniStats: MiniStat[] = [
	{
		iconName: "format-list-checks",
		label: "Progress",
		statusText: "2 of 4",
	},
	{
		iconName: "clipboard-text-outline",
		label: "Quiz accuracy",
		statusText: "Not started",
	},
	{
		iconName: "clock-outline",
		label: "Study time",
		statusText: "45 min",
	},
];

export const moduleDetailMilestones: Milestone[] = [
	{
		id: "overview",
		title: "Overview",
		description: "Completed • 45 mins",
		status: "completed",
	},
	{
		id: "podcast",
		title: "Podcast: Diagnostic Imaging",
		description: "32 min completed",
		status: "completed",
	},
	{
		id: "study-guide",
		title: "Study Guide: EKG Basics",
		description: "In progress • 12/20 pages",
		status: "in-progress",
	},
	{
		id: "quiz",
		title: "Comprehensive Quiz",
		description: "Locked until study guide is complete",
		status: "locked",
	},
];
